import { FormEvent, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Briefcase,
  CheckCircle2,
  Clipboard,
  Download,
  FileSpreadsheet,
  FileText,
  KeyRound,
  Pencil,
  Printer,
  Search,
  UserCheck,
  UserMinus,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  authService,
  ManagedUserFilters,
  ProvisionUserData,
  User,
  UserType,
} from "../services/authService";
import { useAuthStore } from "../stores/authStore";
import { EmptyState, ErrorState, LoadingState } from "../components/PageState";
import { Modal } from "../components/Modal";
import { copyText } from "../utils/clipboard";
import { schoolEmail } from "../utils/schoolEmail";
import {
  useUsernameSuggestion,
  usernameHint,
} from "../hooks/useUsernameSuggestion";
import { exportAsCsv, exportAsExcel, exportAsPdf } from "../utils/exportTable";
import {
  accountProvisioningRoleGroups,
  accountRoleLabels as roleLabels,
  getProvisionableRoleGroups,
} from "./administrationRoles";

function generatePassword() {
  const alphabet =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%";
  const values = crypto.getRandomValues(new Uint32Array(14));
  return `S!${Array.from(values, (value) => alphabet[value % alphabet.length]).join("")}`;
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

function readableError(error: unknown) {
  const data = (
    error as { response?: { data?: Record<string, string[] | string> } }
  )?.response?.data;
  if (!data) return "The account could not be created. Please try again.";
  return Object.entries(data)
    .map(
      ([field, message]) =>
        `${field.replace("_", " ")}: ${Array.isArray(message) ? message.join(" ") : message}`,
    )
    .join(" ");
}

export const ACCOUNT_DIRECTORY_PAGE_SIZE = 50;
const DIRECTORY_PAGE_SIZES = [25, 50, 100];

export type DirectoryFilterState = {
  q: string;
  account_type: "" | NonNullable<ManagedUserFilters["account_type"]>;
  role: string;
  status: "" | NonNullable<ManagedUserFilters["status"]>;
  ordering: NonNullable<ManagedUserFilters["ordering"]>;
};

export const EMPTY_DIRECTORY_FILTERS: DirectoryFilterState = {
  q: "",
  account_type: "",
  role: "",
  status: "",
  ordering: "newest",
};

const ACCOUNT_TYPE_TABS: [DirectoryFilterState["account_type"], string][] = [
  ["", "Everyone"],
  ["staff", "Staff"],
  ["student", "Students"],
  ["parent", "Parents"],
];

const SORT_OPTIONS: [DirectoryFilterState["ordering"], string][] = [
  ["newest", "Newest first"],
  ["oldest", "Oldest first"],
  ["name", "Name A–Z"],
  ["-name", "Name Z–A"],
  ["username", "Username"],
];

const OTHER_ROLE_LABELS: Record<string, string> = {
  admin: "Administrator",
  student: "Student",
  parent: "Parent",
};

export function directoryRoleLabel(role: string) {
  return (
    roleLabels[role] || OTHER_ROLE_LABELS[role] || role.replaceAll("_", " ")
  );
}

/** Only the filters that narrow the directory are sent to the server. */
export function directoryQueryParams(
  filters: DirectoryFilterState,
): ManagedUserFilters {
  const params: ManagedUserFilters = {};
  const q = filters.q.trim();
  if (q) params.q = q;
  if (filters.account_type) params.account_type = filters.account_type;
  if (filters.role) params.role = filters.role;
  if (filters.status) params.status = filters.status;
  if (filters.ordering !== "newest") params.ordering = filters.ordering;
  return params;
}

export function activeDirectoryFilterCount(filters: DirectoryFilterState) {
  return [
    filters.q.trim(),
    filters.account_type,
    filters.role,
    filters.status,
  ].filter(Boolean).length;
}

export function directoryPageSummary(
  count: number,
  requestedPage: number,
  pageSize = ACCOUNT_DIRECTORY_PAGE_SIZE,
) {
  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  const page = Math.min(Math.max(requestedPage, 1), totalPages);
  const start = (page - 1) * pageSize;
  return {
    page,
    totalPages,
    from: count ? start + 1 : 0,
    to: Math.min(start + pageSize, count),
  };
}

export default function Administration() {
  const actor = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();
  // What is typed in the search box; it reaches the filters after a pause so
  // the directory is not re-fetched on every keystroke.
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState(EMPTY_DIRECTORY_FILTERS);
  const [directoryPage, setDirectoryPage] = useState(1);
  const [pageSize, setPageSize] = useState(ACCOUNT_DIRECTORY_PAGE_SIZE);
  const directoryParams = useMemo(() => directoryQueryParams(filters), [filters]);
  const activeFilterCount = activeDirectoryFilterCount(filters);

  const updateFilters = (changes: Partial<DirectoryFilterState>) => {
    setFilters((current) => ({ ...current, ...changes }));
    setDirectoryPage(1);
  };
  const clearFilters = () => {
    setSearchInput("");
    setFilters(EMPTY_DIRECTORY_FILTERS);
    setDirectoryPage(1);
  };

  useEffect(() => {
    if (searchInput === filters.q) return;
    const timer = window.setTimeout(() => updateFilters({ q: searchInput }), 300);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const [created, setCreated] = useState<{
    user: User;
    password: string;
  } | null>(null);
  const [copyFeedback, setCopyFeedback] = useState("");
  // Bumped after a successful creation so the form is rebuilt empty, with a
  // freshly generated temporary password.
  const [formKey, setFormKey] = useState(0);
  const [editing, setEditing] = useState<User | null>(null);
  const [resetting, setResetting] = useState<User | null>(null);
  const [resetCredential, setResetCredential] = useState<{
    username: string;
    password: string;
  } | null>(null);
  const allowedGroups = useMemo(
    () => getProvisionableRoleGroups(actor?.user_type),
    [actor?.user_type],
  );

  // The new login's name, and the username built from it. The username is only
  // left alone once the admin types in it themselves.
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [usernameEdited, setUsernameEdited] = useState(false);
  const {
    base: usernameBase,
    suggested: suggestedUsername,
    emailDomain,
  } = useUsernameSuggestion(firstName, lastName);
  const email = schoolEmail(username, emailDomain);

  useEffect(() => {
    if (usernameEdited) return;
    setUsername(usernameBase);
  }, [usernameBase, usernameEdited]);

  useEffect(() => {
    if (usernameEdited || !suggestedUsername) return;
    setUsername(suggestedUsername);
  }, [suggestedUsername, usernameEdited]);

  const hint = usernameHint(usernameBase, suggestedUsername, usernameEdited);

  const users = useQuery({
    queryKey: [
      "managed-users",
      { ...directoryParams, page: directoryPage, pageSize },
    ],
    queryFn: () =>
      authService.getManagedUsersPage({
        ...directoryParams,
        page: directoryPage,
        page_size: pageSize,
      }),
    keepPreviousData: true,
  });

  const provision = useMutation({
    mutationFn: ({ data }: { data: ProvisionUserData; password: string }) =>
      authService.register(data),
    onSuccess: ({ user }, variables) => {
      setCopyFeedback("");
      setCreated({ user, password: variables.password });
      // Clear the form for the next person: leaving the name behind would
      // offer its username again, which is now taken.
      setFirstName("");
      setLastName("");
      setUsername("");
      setUsernameEdited(false);
      setFormKey((key) => key + 1);
      queryClient.invalidateQueries({ queryKey: ["managed-users"] });
    },
  });

  const changeStatus = useMutation({
    mutationFn: ({
      id,
      isActive,
      reason,
    }: {
      id: number;
      isActive: boolean;
      reason: string;
    }) => authService.setManagedUserStatus(id, isActive, reason),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["managed-users"] }),
  });
  const updateAccount = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Parameters<typeof authService.updateManagedUser>[1];
    }) => authService.updateManagedUser(id, data),
    onSuccess: () => {
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ["managed-users"] });
    },
  });
  const resetPassword = useMutation({
    mutationFn: ({
      id,
      password,
      reason,
    }: {
      id: number;
      password: string;
      reason: string;
    }) => authService.resetManagedUserPassword(id, password, reason),
    onSuccess: (_, values) => {
      setResetCredential({
        username: resetting?.username || "",
        password: values.password,
      });
      setResetting(null);
      queryClient.invalidateQueries({ queryKey: ["managed-users"] });
    },
  });

  const canManage = (user: User) => {
    if (!actor || user.id === actor.id) return false;
    if (actor.user_type === "admin") return true;
    if (actor.user_type === "director")
      return !["admin", "director"].includes(user.user_type);
    return !["admin", "director", "head_teacher"].includes(user.user_type);
  };

  const requestStatusChange = (user: User) => {
    const action = user.is_active ? "deactivate" : "reactivate";
    const reason = window.prompt(
      `Reason to ${action} ${user.full_name || user.username}:`,
    );
    if (!reason) return;
    if (reason.trim().length < 5) {
      window.alert("Please give a reason of at least 5 characters.");
      return;
    }
    if (
      !window.confirm(
        `${action === "deactivate" ? "Deactivate" : "Reactivate"} ${user.full_name || user.username}?`,
      )
    )
      return;
    changeStatus.mutate({
      id: user.id,
      isActive: !user.is_active,
      reason: reason.trim(),
    });
  };

  const submitAccountEdit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editing) return;
    const d = new FormData(event.currentTarget);
    updateAccount.mutate({
      id: editing.id,
      data: {
        first_name: String(d.get("first_name")),
        last_name: String(d.get("last_name")),
        phone_number: String(d.get("phone_number")),
        user_type: String(d.get("user_type")) as UserType,
        reason: String(d.get("reason")),
      },
    });
  };
  const submitPasswordReset = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!resetting) return;
    const d = new FormData(event.currentTarget);
    resetPassword.mutate({
      id: resetting.id,
      password: String(d.get("password")),
      reason: String(d.get("reason")),
    });
  };

  const closeAccountEdit = () => {
    updateAccount.reset();
    setEditing(null);
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCreated(null);
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password"));
    // No email is sent: the server sets it from the username.
    provision.mutate({
      password,
      data: {
        first_name: String(form.get("first_name")).trim(),
        last_name: String(form.get("last_name")).trim(),
        username: String(form.get("username")).trim(),
        phone_number: String(form.get("phone_number")).trim(),
        user_type: String(form.get("user_type")) as UserType,
        password,
      },
    });
  };

  const directoryCount = users.data?.count || 0;
  const pagedUsers = directoryPageSummary(
    directoryCount,
    directoryPage,
    pageSize,
  );

  const [isExporting, setIsExporting] = useState(false);
  const exportDirectory = async (format: "csv" | "excel" | "pdf") => {
    setIsExporting(true);
    try {
      // Exports what the filters show, across every page.
      const accounts = await authService.getManagedUsers(directoryParams);
      const columns = [
        {
          header: "Full name",
          accessor: (user: User) => user.full_name || "Name not provided",
        },
        { header: "Username", accessor: (user: User) => user.username },
        { header: "Email", accessor: (user: User) => user.email },
        {
          header: "Phone",
          accessor: (user: User) => user.phone_number || "",
        },
        {
          header: "Role",
          accessor: (user: User) => directoryRoleLabel(user.user_type),
        },
        {
          header: "Status",
          accessor: (user: User) => (user.is_active ? "Active" : "Inactive"),
        },
      ];
      const filename = `account-directory-${new Date().toISOString().slice(0, 10)}`;
      if (format === "csv") exportAsCsv(`${filename}.csv`, columns, accounts);
      else if (format === "excel")
        exportAsExcel(`${filename}.xls`, "Accounts", columns, accounts);
      else exportAsPdf("Account directory", columns, accounts);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">People & access</h1>
        <p className="text-gray-600">
          Create staff logins first, then complete their employment setup.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border-2 border-primary-500 bg-primary-50 p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white">1</span>
            <span className="text-xs font-bold uppercase tracking-wide text-primary-700">
              Here now
            </span>
          </div>
          <div className="mt-2 font-semibold text-gray-900">Create the login</div>
          <p className="mt-1 text-sm text-gray-600">
            Choose the person’s role and issue temporary credentials.
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-bold text-gray-600">2</span>
            <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
              Staff
            </span>
          </div>
          <div className="mt-2 font-semibold text-gray-900">Complete their staff profile</div>
          <p className="mt-1 text-sm text-gray-600">
            Add a position, hire date, salary, and a functional department only
            when the job belongs to one.
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-bold text-gray-600">3</span>
            <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
              Relevant module
            </span>
          </div>
          <div className="mt-2 font-semibold text-gray-900">Assign their work</div>
          <p className="mt-1 text-sm text-gray-600">
            For example, assign classes, subjects, routes, or service
            responsibilities.
          </p>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
        <form key={formKey} onSubmit={submit} className="card">
          <div className="card-header">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <UserPlus className="h-5 w-5" /> Create a staff login
            </div>
            <p className="text-sm text-gray-500">
              All fields marked * are required. The password is shown after
              creation so you can give it to the user securely.
            </p>
          </div>
          <div className="card-content space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-medium">
                First name *
                <input
                  required
                  name="first_name"
                  autoComplete="off"
                  className="input mt-1"
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                />
              </label>
              <label className="text-sm font-medium">
                Last name *
                <input
                  required
                  name="last_name"
                  autoComplete="off"
                  className="input mt-1"
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                />
              </label>
              <label className="text-sm font-medium">
                Username *
                <input
                  required
                  name="username"
                  autoCapitalize="none"
                  autoComplete="off"
                  className="input mt-1"
                  placeholder="e.g. jnakato"
                  value={username}
                  onChange={(event) => {
                    setUsernameEdited(true);
                    setUsername(event.target.value);
                  }}
                />
                <span className="mt-1 block text-xs font-normal text-gray-500">
                  {hint}
                </span>
                {usernameEdited && suggestedUsername && (
                  <button
                    type="button"
                    className="mt-1 text-xs font-medium text-primary-700 underline"
                    onClick={() => {
                      setUsernameEdited(false);
                      setUsername(suggestedUsername);
                    }}
                  >
                    Use {suggestedUsername} instead
                  </button>
                )}
              </label>
              <label className="text-sm font-medium">
                Work email
                <input
                  readOnly
                  type="email"
                  name="email"
                  tabIndex={-1}
                  className="input mt-1 bg-gray-50 text-gray-600"
                  value={email}
                  placeholder={`jnakato@${emailDomain}`}
                />
                <span className="mt-1 block text-xs font-normal text-gray-500">
                  Every account is reached at its username@{emailDomain}, so
                  this follows the username above.
                </span>
              </label>
              <label className="text-sm font-medium">
                Phone number
                <input
                  name="phone_number"
                  type="tel"
                  className="input mt-1"
                  placeholder="e.g. +256 700 000000"
                />
              </label>
              <label className="text-sm font-medium">
                System role *
                <select
                  required
                  name="user_type"
                  defaultValue=""
                  className="input mt-1"
                >
                  <option value="" disabled>
                    Select what this person does
                  </option>
                  {allowedGroups.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.roles.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
              <label className="text-sm font-medium sm:col-span-2">
                Temporary password *
                <div className="mt-1 flex gap-2">
                  <input
                    required
                    minLength={8}
                    name="password"
                    defaultValue={generatePassword()}
                    className="input font-mono"
                  />
                  <button
                    type="button"
                    className="btn btn-outline shrink-0 px-3"
                    onClick={(event) => {
                      const input = event.currentTarget
                        .previousElementSibling as HTMLInputElement;
                      input.value = generatePassword();
                    }}
                  >
                    Generate new
                  </button>
                </div>
                <span className="mt-1 block text-xs font-normal text-gray-500">
                  Ask the user to change this after their first login.
                </span>
              </label>
            </div>
            {provision.isError && (
              <div
                role="alert"
                className="rounded-md bg-red-50 p-3 text-sm text-red-700"
              >
                {readableError(provision.error)}
              </div>
            )}
            <button
              disabled={provision.isPending}
              className="btn btn-primary px-5 py-2.5"
            >
              {provision.isPending ? "Creating account…" : "Create login"}
            </button>
          </div>
        </form>

        <div className="space-y-4">
          {created ? (
            <div className="rounded-lg border border-green-200 bg-green-50 p-5">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
              <h2 className="mt-3 font-semibold text-green-900">
                Login created for {created.user.full_name}
              </h2>
              <p className="mt-1 text-sm text-green-800">
                Copy these details now and give them only to this user.
              </p>
              <div className="mt-4 rounded bg-white p-3 font-mono text-sm">
                <div>Username: {created.user.username}</div>
                <div>Password: {created.password}</div>
              </div>
              <button
                type="button"
                className="btn btn-outline mt-3 px-3 py-2"
                onClick={async () => {
                  const copied = await copyText(
                    `Portal: ${location.origin}\nUsername: ${created.user.username}\nTemporary password: ${created.password}`,
                  );
                  setCopyFeedback(
                    copied
                      ? "Credentials copied."
                      : "Copy was blocked. Select the credentials above and copy them manually.",
                  );
                }}
              >
                <Clipboard className="mr-2 h-4 w-4" />
                {copyFeedback === "Credentials copied."
                  ? "Copied!"
                  : "Copy credentials"}
              </button>
              {copyFeedback && (
                <p role="status" className="mt-2 text-sm text-green-800">
                  {copyFeedback}
                </p>
              )}
              <Link
                to="/staff"
                className="btn btn-primary mt-3 w-full px-3 py-2"
              >
                Continue to staff profile
              </Link>
            </div>
          ) : (
            <div className="card">
              <div className="card-content pt-6">
                <KeyRound className="h-8 w-8 text-gray-400" />
                <h2 className="mt-3 font-semibold">What happens next?</h2>
                <ol className="mt-3 list-inside list-decimal space-y-2 text-sm text-gray-600">
                  <li>The user can immediately sign in.</li>
                  <li>Go to Staff and select the new eligible user.</li>
                  <li>Add their job information.</li>
                  <li>Assign operational work in the relevant module.</li>
                </ol>
              </div>
            </div>
          )}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
            <strong>Students and parents follow a different process.</strong>
            <p className="mt-1">
              Admit a learner from{" "}
              <Link className="font-semibold underline" to="/students">
                Students
              </Link>
              , then add and link their parent or guardian there. This prevents
              duplicate, unlinked accounts.
            </p>
          </div>
        </div>
      </div>

      {editing && (
        <Modal
          isOpen
          size="2xl"
          title={`Edit ${editing.full_name || editing.username}`}
          onClose={closeAccountEdit}
        >
          <form
            key={editing.id}
            onSubmit={submitAccountEdit}
            className="space-y-4"
          >
            <p className="text-sm text-gray-500">
              Correct identity, contacts, or operational role. The reason is
              retained in access history.
            </p>
            <div className="grid gap-3 md:grid-cols-2">
            {!["parent", "student"].includes(editing.user_type) && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 md:col-span-2">
                Salary, position, hire date, and work location belong to the
                employment profile.{" "}
                <Link
                  to={`/staff?user=${editing.id}`}
                  className="font-semibold underline"
                >
                  Edit employment details in Staff
                </Link>
                .
              </div>
            )}
            <input
              autoFocus
              required
              name="first_name"
              defaultValue={editing.first_name}
              className="input"
              placeholder="First name"
            />
            <input
              required
              name="last_name"
              defaultValue={editing.last_name}
              className="input"
              placeholder="Last name"
            />
            <label className="text-xs font-medium text-gray-500">
              Email
              <input
                readOnly
                type="email"
                tabIndex={-1}
                value={editing.email}
                className="input mt-1 bg-gray-50 text-gray-600"
                placeholder="Email"
              />
              <span className="mt-1 block font-normal">
                Set from the username; it is not edited here.
              </span>
            </label>
            <input
              name="phone_number"
              defaultValue={editing.phone_number || ""}
              className="input"
              placeholder="Phone"
            />
            <select
              required
              name="user_type"
              defaultValue={editing.user_type}
              className="input"
            >
              {allowedGroups
                .flatMap((group) => group.roles)
                .map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
            </select>
            <input
              required
              minLength={5}
              name="reason"
              className="input"
              placeholder="Reason for change"
            />
            {updateAccount.isError && (
              <div
                role="alert"
                className="rounded-md bg-red-50 p-3 text-sm text-red-700 md:col-span-2"
              >
                {readableError(updateAccount.error)}
              </div>
            )}
            <div className="flex justify-end gap-2 border-t pt-4 md:col-span-2">
              <button
                type="button"
                onClick={closeAccountEdit}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                disabled={updateAccount.isPending}
                className="btn btn-primary"
              >
                {updateAccount.isPending ? "Saving…" : "Save account"}
              </button>
            </div>
            </div>
          </form>
        </Modal>
      )}

      {resetting && (
        <form
          onSubmit={submitPasswordReset}
          className="card border-2 border-yellow-200"
        >
          <div className="card-header">
            <h2 className="text-lg font-semibold">
              Reset password for {resetting.full_name || resetting.username}
            </h2>
            <p className="text-sm text-gray-500">
              Issue a temporary password. The user will be restricted to
              changing it at their next login.
            </p>
          </div>
          <div className="card-content grid gap-3 md:grid-cols-2">
            <input
              required
              minLength={8}
              name="password"
              defaultValue={generatePassword()}
              className="input font-mono"
            />
            <input
              required
              minLength={5}
              name="reason"
              className="input"
              placeholder="Reason for reset"
            />
            <div className="flex gap-2 md:col-span-2">
              <button
                disabled={resetPassword.isPending}
                className="btn btn-primary"
              >
                Set temporary password
              </button>
              <button
                type="button"
                onClick={() => setResetting(null)}
                className="btn btn-outline"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}

      {resetCredential && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <strong>Temporary credentials ready</strong>
          <div className="mt-2 font-mono text-sm">
            Username: {resetCredential.username}
            <br />
            Password: {resetCredential.password}
          </div>
          <p className="mt-2 text-sm">
            Copy this now. The password is not retrievable after this screen is
            closed.
          </p>
          <button
            className="btn btn-outline mt-2"
            onClick={() => setResetCredential(null)}
          >
            I have copied it
          </button>
        </div>
      )}

      <div className="card">
        <div className="card-header pb-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-lg font-semibold">
                <Users className="h-5 w-5" /> Account directory
              </div>
              <p className="text-sm text-gray-500">
                Confirm that each person has the correct access role.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-gray-500">Export</span>
              {(
                [
                  ["csv", FileText, "CSV", "Download the filtered directory as CSV"],
                  ["excel", FileSpreadsheet, "Excel", "Download the filtered directory as Excel"],
                  ["pdf", Printer, "PDF", "Print or save the filtered directory as PDF"],
                ] as const
              ).map(([format, Icon, label, title]) => (
                <button
                  key={format}
                  type="button"
                  disabled={isExporting || !directoryCount}
                  onClick={() => exportDirectory(format)}
                  className="pager-button h-8"
                  title={title}
                >
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>
          </div>
          {isExporting && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
              <Download className="h-3.5 w-3.5 animate-bounce" /> Preparing the
              filtered directory export…
            </p>
          )}
        </div>
        <div className="card-content space-y-4">
          <div className="filter-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div
                role="group"
                aria-label="Account type"
                className="segmented"
              >
                {ACCOUNT_TYPE_TABS.map(([value, label]) => (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={filters.account_type === value}
                    onClick={() =>
                      updateFilters({
                        account_type: value,
                        // Students and parents have one role each, so a staff
                        // role filter would only ever empty the list.
                        role:
                          value === "student" || value === "parent"
                            ? ""
                            : filters.role,
                      })
                    }
                    className="segmented-option"
                  >
                    {label}
                  </button>
                ))}
              </div>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                >
                  <X className="h-3.5 w-3.5" /> Clear {activeFilterCount}{" "}
                  {activeFilterCount === 1 ? "filter" : "filters"}
                </button>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(16rem,2fr)_repeat(3,minmax(0,1fr))]">
              <label className="filter-field sm:col-span-2 xl:col-span-1">
                Search
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                  <input
                    type="search"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    className="input-compact pl-8"
                    placeholder="Name, username, email, phone or role"
                  />
                </div>
              </label>
              <label className="filter-field">
                Role
                <select
                  value={filters.role}
                  disabled={
                    filters.account_type === "student" ||
                    filters.account_type === "parent"
                  }
                  onChange={(event) =>
                    updateFilters({ role: event.target.value })
                  }
                  className="input-compact"
                >
                  <option value="">All roles</option>
                  {accountProvisioningRoleGroups.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.roles.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                  <optgroup label="System">
                    <option value="admin">Administrator</option>
                  </optgroup>
                </select>
              </label>
              <label className="filter-field">
                Status
                <select
                  value={filters.status}
                  onChange={(event) =>
                    updateFilters({
                      status: event.target.value as DirectoryFilterState["status"],
                    })
                  }
                  className="input-compact"
                >
                  <option value="">Any status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>
              <label className="filter-field">
                Sort by
                <select
                  value={filters.ordering}
                  onChange={(event) =>
                    updateFilters({
                      ordering: event.target
                        .value as DirectoryFilterState["ordering"],
                    })
                  }
                  className="input-compact"
                >
                  {SORT_OPTIONS.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {users.isLoading ? (
            <LoadingState label="Loading accounts…" />
          ) : users.isError ? (
            <ErrorState
              message="Accounts could not be loaded."
              retry={() => users.refetch()}
            />
          ) : !users.data?.results.length ? (
            <EmptyState
              title="No matching accounts"
              message={
                activeFilterCount
                  ? "Nothing matches these filters. Clear a filter or try another search."
                  : "No accounts have been created yet."
              }
            />
          ) : (
            <div className="space-y-3">
              <div className={`sheet-wrap ${users.isFetching ? "opacity-60" : ""}`}>
                <table className="sheet">
                  <thead>
                    <tr>
                      <th className="sheet-rownum">#</th>
                      <th>Name</th>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Joined</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.data.results.map((user, index) => (
                      <tr key={user.id}>
                        <td className="sheet-rownum">
                          {pagedUsers.from + index}
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-100 text-[9px] font-semibold text-primary-700">
                              {getInitials(user.full_name || user.username)}
                            </span>
                            <span className="font-medium text-gray-900">
                              {user.full_name || (
                                <span className="italic text-gray-400">
                                  No name
                                </span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="font-mono text-xs text-gray-700">
                          {user.username}
                        </td>
                        <td className="max-w-[16rem] truncate text-gray-600" title={user.email}>
                          {user.email || "—"}
                        </td>
                        <td className="tabular-nums text-gray-600">
                          {user.phone_number || "—"}
                        </td>
                        <td
                          className="max-w-[13rem] truncate"
                          title={directoryRoleLabel(user.user_type)}
                        >
                          <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-[11px] font-medium text-secondary-800">
                            {directoryRoleLabel(user.user_type)}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`inline-flex items-center gap-1 text-xs font-medium ${user.is_active ? "text-green-700" : "text-gray-500"}`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${user.is_active ? "bg-green-500" : "bg-gray-400"}`}
                            />
                            {user.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="tabular-nums text-xs text-gray-500">
                          {user.date_joined
                            ? new Date(user.date_joined).toLocaleDateString()
                            : "—"}
                        </td>
                        <td>
                          {canManage(user) ? (
                            <div className="flex items-center justify-end gap-0.5">
                              <button
                                type="button"
                                onClick={() => {
                                  updateAccount.reset();
                                  setEditing(user);
                                }}
                                className="sheet-action text-primary-700"
                                title="Edit account"
                                aria-label={`Edit ${user.full_name || user.username}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              {!["parent", "student"].includes(
                                user.user_type,
                              ) && (
                                <Link
                                  to={`/staff?user=${user.id}`}
                                  className="sheet-action text-blue-700"
                                  title="Employment details"
                                  aria-label={`Employment details for ${user.full_name || user.username}`}
                                >
                                  <Briefcase className="h-3.5 w-3.5" />
                                </Link>
                              )}
                              <button
                                type="button"
                                onClick={() => setResetting(user)}
                                className="sheet-action text-yellow-700"
                                title="Reset password"
                                aria-label={`Reset password for ${user.full_name || user.username}`}
                              >
                                <KeyRound className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                disabled={changeStatus.isPending}
                                onClick={() => requestStatusChange(user)}
                                className={`sheet-action ${user.is_active ? "text-red-700" : "text-green-700"}`}
                                title={user.is_active ? "Deactivate" : "Reactivate"}
                                aria-label={`${user.is_active ? "Deactivate" : "Reactivate"} ${user.full_name || user.username}`}
                              >
                                {user.is_active ? (
                                  <UserMinus className="h-3.5 w-3.5" />
                                ) : (
                                  <UserCheck className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </div>
                          ) : (
                            <span className="block text-right text-xs text-gray-400">
                              Protected
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-600">
                <div className="flex flex-wrap items-center gap-4">
                  <span>
                    Showing{" "}
                    <strong className="font-semibold text-gray-900">
                      {pagedUsers.from}–{pagedUsers.to}
                    </strong>{" "}
                    of{" "}
                    <strong className="font-semibold text-gray-900">
                      {directoryCount}
                    </strong>{" "}
                    accounts
                  </span>
                  <label className="inline-flex items-center gap-1.5">
                    Rows per page
                    <select
                      value={pageSize}
                      onChange={(event) => {
                        setPageSize(Number(event.target.value));
                        setDirectoryPage(1);
                      }}
                      className="h-7 rounded border border-gray-300 bg-white px-1.5 text-xs"
                    >
                      {DIRECTORY_PAGE_SIZES.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="pager-button"
                    disabled={pagedUsers.page === 1}
                    onClick={() => setDirectoryPage(pagedUsers.page - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    Page {pagedUsers.page} of {pagedUsers.totalPages}
                  </span>
                  <button
                    type="button"
                    className="pager-button"
                    disabled={pagedUsers.page === pagedUsers.totalPages}
                    onClick={() => setDirectoryPage(pagedUsers.page + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
